import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Zap, Mail, Building2 } from "lucide-react";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ s }) => {
  const map = {
    new: { bg: "#005fca", label: "NEW" },
    reviewing: { bg: "#fdb429", label: "REVIEWING" },
    contacted: { bg: "#e77300", label: "CONTACTED" },
    closed: { bg: "#8ba3cc", label: "CLOSED" },
  };
  const it = map[s] || map.new;
  return <span className="px-2 py-1 text-[10px] font-mono-alt tracking-widest" style={{ background: it.bg, color: "#020815" }}>{it.label}</span>;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem("alight_admin_token");
  const [requests, setRequests] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState("requests");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    if (!token) { navigate("/admin/login"); return; }
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        axios.get(`${API}/admin/sponsorship/requests`, authHeaders),
        axios.get(`${API}/admin/contact/messages`, authHeaders),
      ]);
      setRequests(r.data);
      setContacts(c.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem("alight_admin_token"); navigate("/admin/login"); }
      else toast.error("Failed to load data");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/sponsorship/requests/${id}`, { status }, authHeaders);
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const logout = () => { localStorage.removeItem("alight_admin_token"); navigate("/admin/login"); };

  const stats = {
    total: requests.length,
    new: requests.filter((r) => r.status === "new").length,
    contacts: contacts.length,
  };

  return (
    <div className="min-h-screen">
      <Seo title="Admin Dashboard — Alight Racing" description="Alight Racing admin console." noindex />
      <header className="border-b border-[#022a67] bg-[#020815]/90 backdrop-blur-xl sticky top-0 z-40" data-testid="admin-header">
        <div className="container-ar h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 relative">
              <div className="absolute inset-0 bg-[#e77300] rotate-45" />
              <div className="absolute inset-1 bg-[#020815] rotate-45 flex items-center justify-center"><Zap className="w-3 h-3 text-[#e77300] -rotate-45" /></div>
            </div>
            <div className="font-display uppercase tracking-widest">Alight · Admin</div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="p-2 hover:text-[#e77300]" data-testid="admin-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
            <button onClick={logout} className="btn-racing secondary text-xs" data-testid="admin-logout"><span>Logout <LogOut className="w-4 h-4" /></span></button>
          </div>
        </div>
      </header>

      <main className="container-ar py-10">
        <h1 className="h2-section text-3xl md:text-4xl mb-6">Sponsorship Console</h1>

        <div className="grid grid-cols-3 gap-3 mb-8">
          <div className="tele-card p-5"><div className="eyebrow">Total Requests</div><div className="font-display text-4xl mt-1">{stats.total}</div></div>
          <div className="tele-card p-5"><div className="eyebrow">New</div><div className="font-display text-4xl text-[#e77300] mt-1">{stats.new}</div></div>
          <div className="tele-card p-5"><div className="eyebrow">Contact Messages</div><div className="font-display text-4xl text-[#005fca] mt-1">{stats.contacts}</div></div>
        </div>

        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab("requests")} className={`px-4 py-2 font-display uppercase tracking-widest text-sm ${tab === "requests" ? "text-[#e77300] border-b-2 border-[#e77300]" : "text-white/60"}`} data-testid="admin-tab-requests">Sponsorship Requests</button>
          <button onClick={() => setTab("contacts")} className={`px-4 py-2 font-display uppercase tracking-widest text-sm ${tab === "contacts" ? "text-[#e77300] border-b-2 border-[#e77300]" : "text-white/60"}`} data-testid="admin-tab-contacts">Contact Messages</button>
        </div>

        {tab === "requests" ? (
          <div className="space-y-3" data-testid="admin-requests-list">
            {requests.length === 0 && <div className="tele-card p-6 text-center text-white/60">No sponsorship requests yet.</div>}
            {requests.map((r) => (
              <div key={r.id} className="tele-card p-5" data-testid={`req-row-${r.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="font-display text-lg uppercase">{r.company}</div>
                      <div className="text-xs text-[#8ba3cc]">{r.full_name} · {r.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge s={r.status} />
                    <select className="ar-input py-1 text-xs w-auto" value={r.status} onChange={(e) => updateStatus(r.id, e.target.value)} data-testid={`req-status-${r.id}`}>
                      <option value="new">new</option><option value="reviewing">reviewing</option><option value="contacted">contacted</option><option value="closed">closed</option>
                    </select>
                    <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="text-xs font-mono-alt tracking-widest text-[#e77300] uppercase" data-testid={`req-expand-${r.id}`}>{expanded === r.id ? "Hide" : "Details"}</button>
                  </div>
                </div>
                {expanded === r.id && (
                  <div className="mt-5 pt-5 border-t border-[#022a67] grid md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-1 text-white/80">
                      <div><b>Position:</b> {r.position || "—"}</div>
                      <div><b>Phone:</b> {r.phone || "—"}</div>
                      <div><b>Country:</b> {r.country || "—"}</div>
                      <div><b>Website:</b> {r.website || "—"}</div>
                      <div><b>Industry:</b> {r.industry || "—"}</div>
                      <div><b>Partnership:</b> {r.partnership_type || "—"}</div>
                      <div><b>Budget:</b> {r.budget_range || "—"}</div>
                      <div><b>Contact:</b> {r.preferred_contact || "—"}</div>
                      <div><b>Objective:</b> {r.objective || "—"}</div>
                    </div>
                    <div>
                      {r.selected_combos?.length > 0 && (
                        <>
                          <div className="eyebrow mb-1">Combos</div>
                          <ul className="text-white/80 list-disc list-inside mb-3">{r.selected_combos.map((c, i) => <li key={i}>{c.title}</li>)}</ul>
                        </>
                      )}
                      {r.selected_benefits?.length > 0 && (
                        <>
                          <div className="eyebrow mb-1">Benefits</div>
                          <ul className="text-white/80 list-disc list-inside mb-3">{r.selected_benefits.map((b, i) => <li key={i}>{b.title} — {b.category}</li>)}</ul>
                        </>
                      )}
                      {r.message && <div><div className="eyebrow mb-1">Message</div><p className="text-white/80 whitespace-pre-wrap">{r.message}</p></div>}
                      <div className="text-[10px] font-mono-alt text-[#8ba3cc] tracking-widest mt-3">{new Date(r.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-contacts-list">
            {contacts.length === 0 && <div className="tele-card p-6 text-center text-white/60">No contact messages yet.</div>}
            {contacts.map((c) => (
              <div key={c.id} className="tele-card p-5" data-testid={`contact-row-${c.id}`}>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex-1">
                    <div className="font-display text-lg uppercase flex items-center gap-2"><Building2 className="w-4 h-4 text-[#e77300]" />{c.name} {c.company && <span className="text-sm text-[#8ba3cc]">· {c.company}</span>}</div>
                    <div className="text-xs text-[#8ba3cc] flex items-center gap-2 mt-1"><Mail className="w-3 h-3" /> {c.email} · {c.reason}</div>
                  </div>
                  <div className="text-[10px] font-mono-alt text-[#8ba3cc] tracking-widest">{new Date(c.created_at).toLocaleString()}</div>
                </div>
                <p className="text-sm text-white/80 mt-3 whitespace-pre-wrap">{c.message}</p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
